import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import { validImageTypes, validVideoTypes } from "../../components/Enums";
import { useVideoPlayer } from "src/scenes/BodyAndMind/hooks/VideoPreview";
import { RemoveMediaButton } from "src/scenes/BodyAndMind/components/style";
import CloseIcon from "src/img/CloseButton.png";
import {
    Text,
    Toast,
    ImageCropModal,
    VideoCropModal,
    VideoBottomControls,
    VideoContainer,
    VideoControlsBar,
    ControlButton,
    CustomRange,
    PauseIcon,
    PlayIcon,
    VolumeIcon,
    FullscreenIcon,
    formatTime,
    PortalFormLabel,
} from "src/components";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";

const ClickableDivBox = styled.div`
    width: 205px;
    height: 364px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e9ebef;
    border-radius: 15px;
    background-color: #f8f8f8;
    cursor: pointer;
    position: relative;
`;

const BrowseFileButton = styled.div`
    width: 140px;
    height: 37.5px;
    background-color: #000;
    box-shadow: 0px 3px 6px #00000029;
    border-radius: 7px;
    color: #fff;
    text-align: center;
    align-items: center;
    display: flex;
    justify-content: center;
    font-weight: 400;
    font-size: 0.875rem;
    font-family: "Manrope", "Helvetica", sans-serif;
`;

const ImageBoxParent = styled.div`
    display: flex;
    position: relative;
    align-items: end;
    justify-content: center;
`;

const ImageBox = styled.img`
    width: 205px;
    height: 364px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e0e0e0;
    border-radius: 15px;
    background-color: #e8e8e8;
    cursor: pointer;
    object-fit: cover;
    object-position: center;
`;

const ChangeAsset = styled.div`
    margin-bottom: 20px;
    background-color: black;
    border: none;
    border-radius: 7px;
    cursor: pointer;
    box-shadow: 0px 3px 6px #00000029;
    width: 166px;
    height: 38px;
    font-size: 0.875rem;
    font-family: "Manrope", "Helvetica", sans-serif;
    font-weight: 400;
    color: white;
    z-index: 1;
    position: absolute;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const renderAssetFieldPreview = (verticalAsset, assetType) => {
    if (assetType === "image" || validImageTypes.includes(assetType)) {
        return (
            <ImageBoxParent>
                <ImageBox src={verticalAsset} alt="Preview" />
                <ChangeAsset>Change Image/Video</ChangeAsset>
            </ImageBoxParent>
        );
    }
    return <>Unsupported Asset Type</>;
};

const AdVerticalImageUpload = ({
    setFormVerticalAsset,
    setImageChanged,
    asset,
    setFormVerticalAssetType,
    assetType,
}) => {
    const [previewUrl, setPreviewUrl] = useState(null); 
    const [fileTypeOfPreview, setFileTypeOfPreview] = useState(assetType || null);
    const [isCroppingImage, setIsCroppingImage] = useState(false);
    const [isCroppingVideo, setIsCroppingVideo] = useState(false);

    const fileInputRef = useRef(null);
    const objectUrlRef = useRef(null);

    const {
        videoRef,
        containerRef,
        muted,
        currentTime,
        duration,
        isPlaying,
        isHovered,
        setIsHovered,
        togglePlayPause,
        useCrop: videoUseCrop,
        toggleMute,
        toggleFullscreen,
        handleProgressChange,
        progressValue,
        validTrimStart,
        videoDimensions
    } = useVideoPlayer({
        videoSrc: fileTypeOfPreview === "video" ? previewUrl : null,
        trimTimeStart: asset?.trimStart,
        trimTimeEnd: asset?.trimEnd,
        crop: asset?.crop
    });

    // Cleanup any objectURL when component unmount
    useEffect(() => {
        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };
    }, []);

    // Update preview when asset.primaryAssetBlob changes
    useEffect(() => {
        if (asset.verticalAssetBlob) {
            setPreviewUrl(asset.verticalAssetBlob);
            setFileTypeOfPreview(assetType || "image");
        }
    }, [asset.verticalAssetBlob, assetType]);

    // Handle file select
    const handleFileSelect = useCallback((file) => {
        if (!file) return;
        const { type } = file;

        if (validImageTypes.includes(type)) {
            const imageUrl = URL.createObjectURL(file);
            objectUrlRef.current = imageUrl;
            setPreviewUrl(imageUrl);
            setFileTypeOfPreview("image");

            setIsCroppingImage(true);
            setIsCroppingVideo(false);

        } else if (validVideoTypes.includes(type)) {
            const videoUrl = URL.createObjectURL(file);
            objectUrlRef.current = videoUrl;
            setPreviewUrl(videoUrl);
            setFileTypeOfPreview("video");

            setIsCroppingVideo(true);
            setIsCroppingImage(false);

        } else {
            Toast.error("Unsupported file type. Images: JPG, PNG. Videos: MP4.");
        }
    }, []);


    const handleInputChange = (e) => {
        const files = e.target.files;
        if (files && files[0]) {
            handleFileSelect(files[0]);
            // reset input so selecting same file again triggers change
            e.target.value = "";
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const dtFiles = e.dataTransfer.files;
        if (dtFiles && dtFiles[0]) {
            handleFileSelect(dtFiles[0]);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleRemove = (e) => {
        e?.stopPropagation();
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
        setPreviewUrl(null);
        setFileTypeOfPreview(null);
        setIsCroppingImage(false);
        setIsCroppingVideo(false);

        setFormVerticalAsset({});
        setImageChanged(true);
        setFormVerticalAssetType(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = null;
            fileInputRef.current.click();
        }
    };

    // When cropping of image completes
    const handleImageCropComplete = (croppedFile) => {
        if (!croppedFile) {
            setIsCroppingImage(false);
            return;
        }
        // cleanup old
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
        // Create new preview
        const newUrl = URL.createObjectURL(croppedFile);
        objectUrlRef.current = newUrl;
        setPreviewUrl(newUrl);
        setFileTypeOfPreview("image");
        setFormVerticalAsset(prev => ({
            ...prev,
            verticalAssetFile: croppedFile,
            verticalAssetBlob: newUrl
        }));
        setFormVerticalAssetType("image");
        setImageChanged(true);
        setIsCroppingImage(false);
    };

    // When cropping / trimming of video completes
    const handleVideoCropComplete = (croppedFile, crop, trimStart, trimEnd, aspectRatioLabel) => {
        if (!croppedFile) {
            setIsCroppingVideo(false);
            return;
        }
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
        const newUrl = URL.createObjectURL(croppedFile);
        objectUrlRef.current = newUrl;
        setPreviewUrl(newUrl);
        setFileTypeOfPreview("video");
        setFormVerticalAsset(prev => ({
            ...prev,
            verticalAssetFile: croppedFile,
            verticalAssetBlob: newUrl,
            crop,
            trimStart,
            trimEnd,
            aspectRatio: aspectRatioLabel
        }));
        setFormVerticalAssetType("video");
        setImageChanged(true);
        setIsCroppingVideo(false);
    };


    let cropStyles = {};
    if (
        asset?.crop?.croppedAreaPixels &&
        videoDimensions.width &&
        videoDimensions.height &&
        containerRef.current
    ) {
        const { x, y, width: cropW, height: cropH } = asset.crop.croppedAreaPixels;
        const { width: vidW, height: vidH } = videoDimensions;
        const { width: contW, height: contH } = containerRef.current.getBoundingClientRect();

        const scale = Math.min(contW / cropW, contH / cropH);

        cropStyles = {
            width: `${vidW * scale}px`,
            height: `${vidH * scale}px`,
            top: `${-y * scale}px`,
            left: `${-x * scale}px`,
            position: "absolute",
            transformOrigin: "top left",
            objectFit: "cover",
        };
    }

    const getPreviewForRender = () => {
        if (fileTypeOfPreview === "image") {
            return renderAssetFieldPreview(previewUrl, "image");
        }
        if (fileTypeOfPreview === "video") {
            return (
                <VideoContainer
                    ref={containerRef}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    style={{
                        width: "364px",
                        height: "100%",
                        borderRadius: "15px",
                        border: "1px solid #E0E0E0",
                        backgroundColor: "#E8E8E8",
                        boxShadow: "0px 5px 5px #00000026",
                        overflow: videoUseCrop ? "hidden" : "visible",
                        position: "relative",
                        cursor: isPlaying && !isHovered ? "none" : "default",
                    }}
                >
                    {videoUseCrop ? (
                        <>
                            <div
                                style={{
                                    width: "364px",
                                    height: "100%",
                                    borderRadius: "15px",
                                    overflow: "hidden",
                                    position: "relative",
                                }}
                            >
                                <video
                                    key={previewUrl}
                                    ref={videoRef}
                                    muted={muted}
                                    controls={false}
                                    style={{
                                        ...cropStyles,
                                        position: "absolute",
                                    }}
                                >
                                    <source src={previewUrl} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            </div>

                            <VideoBottomControls show={!isPlaying || isHovered} onClick={(e) => e.stopPropagation()}>
                                <VideoControlsBar interactive fontSize="0.2rem">
                                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                        <ControlButton onClick={togglePlayPause}>
                                            {isPlaying ? <PauseIcon /> : <PlayIcon />}
                                        </ControlButton>
                                        <span style={{ fontSize: "0.85rem", fontFamily: "Manrope, sans-serif" }}>
                                            {formatTime(Math.max(0, currentTime - validTrimStart))} / {formatTime(duration)}
                                        </span>
                                    </div>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        <ControlButton onClick={toggleMute}>
                                            <VolumeIcon muted={muted} />
                                        </ControlButton>
                                        <ControlButton onClick={toggleFullscreen}>
                                            <FullscreenIcon />
                                        </ControlButton>
                                    </div>
                                </VideoControlsBar>

                                <CustomRange
                                    value={progressValue}
                                    max={duration}
                                    min={0}
                                    step="0.1"
                                    onChange={handleProgressChange}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </VideoBottomControls>
                        </>
                    ) : (
                        <video
                            key={previewUrl}
                            ref={videoRef}
                            muted={muted}
                            controls
                            style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "15px",
                                objectFit: "cover",
                            }}
                        >
                            <source src={previewUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    )}
                </VideoContainer>
            );
        }
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "205px",
                }}
            >
                <BrowseFileButton>Browse File</BrowseFileButton>
            </div>
        );
    };

    return (
        <div>
            <PortalFormLabel>
                Upload Vertical Asset (Optional)
            </PortalFormLabel>
            <Text size="0.938rem" color="#612684" align="left">Click browse file to upload your image or video.</Text>
            <Text
                size="0.875rem"
                color="#808080"
                align="left"
                style={{ wrap: "auto", maxHeight: "48px", maxWidth: "290px" }}
            >
                Supported formats: JPG, PNG, MP4. Aspect Ratio 9:16 - 1080px x 1920px
            </Text>

            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", marginBottom: "20px" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <input
                        type="file"
                        accept=".png, .jpg, .jpeg, .mp4"
                        ref={fileInputRef}
                        onChange={handleInputChange}
                        style={{ display: "none" }}
                    />
                    <ClickableDivBox 
                        onClick={() => fileInputRef.current && fileInputRef.current.click()} 
                        onDragOver={handleDragOver} 
                        onDrop={handleDrop}
                    >
                        {getPreviewForRender()}

                        {previewUrl && (
                            <RemoveMediaButton onClick={handleRemove} style={{ top: "-10px" }}>
                                <img src={CloseIcon} alt="close button" width={24} height={24} />
                            </RemoveMediaButton>
                        )}
                    </ClickableDivBox>
                </div>
            </div>

            { isCroppingImage && previewUrl && fileTypeOfPreview === "image" && (
                <ImageCropModal
                    imageSrc={previewUrl}
                    isOpen={true}
                    onClose={(shouldClear) => {
                        setIsCroppingImage(false);
                        if (shouldClear) {
                            handleRemove();
                        }
                    }}
                    onCropComplete={handleImageCropComplete}
                    aspectRatios={[
                        { width: 9, height: 16, label: "9:16", standardName: ASPECT_RATIO_LABEL.PORTRAIT },
                    ]}
                    allowedOrientations={["portrait"]}
                    lockedAspectRatio={undefined}
                />
            )}

            { isCroppingVideo && previewUrl && fileTypeOfPreview === "video" && (
                <VideoCropModal
                    videoSrc={previewUrl}
                    isOpen={true}
                    onClose={(shouldClear = false) => {
                        setIsCroppingVideo(false);
                        if (shouldClear) {
                            handleRemove();
                        }
                    }}
                    onCropComplete={handleVideoCropComplete}
                    aspectRatios={[
                        { width: 9, height: 16, label: "9:16", standardName: ASPECT_RATIO_LABEL.PORTRAIT },
                    ]}
                    allowedOrientations={["portrait"]}
                    lockedAspectRatio={undefined}
                />
            )}
        </div>
    );
};

export default AdVerticalImageUpload;
